// API functions for PDF editor feature
import { POST, GET, DELETE, PATCH } from '@/shared/api/requests';

// ============================================
// Types based on backend serializers
// ============================================

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

export interface PDFPage {
  id: string;
  pdf_file: string;
  page_number: number;
  width: number;
  height: number;
  rotation: number;
  dpi: number;
  preview_url: string | null;
  metadata: Record<string, any>;
  drawing_number: string;
  title: string;
  discipline: string;
  ocr_text: string;
  ocr_confidence: number | null;
  drawing_refs?: Record<string, any>;
  sheet_match_confidence?: number | null;
  sheet_match_method?: 'auto' | 'manual' | string;
  sheet_id?: string | null;
  sheet_identity_key?: string | null;
  sheet_revision_number?: number | null;
  sheet_revision_label?: string | null;
  sheet_is_current_revision?: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface PDFFile {
  id: string;
  project: number;
  project_name: string;
  file: string;
  // Direct signed GCS URL (15 min lifetime). Use as-is with fetch() / PDF.js.
  file_url: string;
  original_filename: string;
  page_count: number;
  status: 'uploaded' | 'processing' | 'ready' | 'error';
  processing_error: string;
  uploaded_by: User;
  pages: PDFPage[];
  created_at: string;
  updated_at: string;
}

export interface DrawingUploadBatchItem {
  id: string;
  pdf_file: PDFFileList | null;
  first_page: PDFPage | null;
  original_filename: string;
  gcs_object_path: string;
  file_size: number | null;
  content_type: string;
  status: string;
  processing_error: string;
  sort_order: number;
  task_id: string;
  metadata: Record<string, any>;
  signed_upload_url?: string;
  created_at: string;
  updated_at: string;
}

export interface DrawingUploadBatch {
  id: string;
  project: number;
  status: string;
  total_files: number;
  uploaded_files: number;
  processing_files: number;
  completed_files: number;
  failed_files: number;
  metadata: Record<string, any>;
  items: DrawingUploadBatchItem[];
  created_at: string;
  updated_at: string;
}

export interface DrawingUploadBatchInitResponse {
  id: string;
  project: number;
  status: string;
  total_files: number;
  items: DrawingUploadBatchItem[];
}

export interface PDFFileList {
  id: string;
  project: number;
  project_name: string;
  // Direct signed GCS URL (15 min lifetime). Use as-is with fetch() / PDF.js.
  file_url: string;
  original_filename: string;
  page_count: number;
  status: 'uploaded' | 'processing' | 'ready' | 'error';
  processing_error: string;
  uploaded_by: User;
  // Unique disciplines across all pages (in page order). May be empty pre-processing.
  disciplines?: string[];
  // First non-empty drawing number / title across pages, for nicer list labels.
  primary_drawing_number?: string;
  primary_title?: string;
  primary_page_id?: string | null;
  primary_revision_label?: string;
  linked_rfi_count?: number;
  linked_submittal_count?: number;
  linked_issue_count?: number;
  shared_markup_count?: number;
  last_activity_at?: string;
  can_manage_drawings?: boolean;
  created_at: string;
  updated_at: string;
}

export interface LayerDocument {
  id: string;
  pdf_file: string;
  pdf_filename: string;
  version: number;
  layers: Layer[];
  created_by: User;
  created_at: string;
  last_modified: string;
  last_modified_by: User;
}

export interface Layer {
  id: string;
  layer_document: string;
  page_number: number;
  layer_type: 'shape' | 'path' | 'text' | 'annotation' | 'measurement' | 'image' | 'hyperlink';
  data: Record<string, any>;
  z_index: number;
  visible: boolean;
  markup_scope: 'personal' | 'shared';
  punch_list_item: string | null;
  observation: string | null;
  submittal: string | null;
  rfi: string | null;
  created_by?: User;
  created_at?: string;
  updated_at?: string;
}

export interface LayerMutationOptions {
  expected_version?: number;
  client_mutation_id?: string;
}

export interface PDFRevisionOption {
  id: string;
  pdf_file_id: string;
  pdf_page_number: number;
  sheet_id?: string;
  label: string;
  revision_number: number;
  created_at: string;
  original_filename: string;
  is_selected: boolean;
  is_current?: boolean;
  sheet_revision_id?: string;
  published_at?: string | null;
  superseded_at?: string | null;
}

export interface SheetRevisionDetail {
  id: string;
  sheet: {
    id: string;
    project: number;
    identity_key: string;
    drawing_number: string;
    title: string;
    discipline: string;
    current_revision: string | null;
    created_at: string;
    updated_at: string;
  };
  pdf_file_id: string;
  pdf_page_id: string;
  pdf_page_number: number;
  revision_number: number;
  revision_label: string;
  is_current: boolean;
  published_at: string | null;
  superseded_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DrawingSheetOption {
  id: string;
  project: number;
  identity_key: string;
  drawing_number: string;
  title: string;
  discipline: string;
  current_revision: string | null;
  created_at: string;
  updated_at: string;
}

export interface PunchListItemRef {
  id: string;
  title: string;
  status: string;
  priority?: string;
  assignee?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  } | null;
  due_date?: string | null;
  is_private?: boolean;
  created_at?: string;
  description?: string;
  item_type?: { id?: string; name?: string } | null;
  location?: { id?: string; name?: string } | null;
  trade?: { id?: string; name?: string } | null;
}

export interface ObservationRef {
  id: string;
  number: number;
  title: string;
  status: string;
  priority?: string;
  assignee?: {
    id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  } | null;
  due_date?: string | null;
  is_private?: boolean;
  created_at?: string;
  description?: string;
  observation_type?: { id?: string; name?: string } | null;
  location?: { id?: string; name?: string } | null;
  trade?: { id?: string; name?: string } | null;
}

export interface SubmittalRef {
  id: string;
  submittal_number: string;
  title: string;
  status: string;
  priority?: string;
  due_date?: string | null;
  responsible_contractor?: string;
  revision_number?: number;
  trade?: { id?: string; name?: string } | null;
}

export interface RFIRef {
  id: string;
  rfi_number: string;
  title: string;
  status: string;
  priority: string;
  due_date?: string | null;
  trade?: { id?: string; name?: string } | null;
}

export interface PDFUploadMetadataPreview {
  page_count: number;
  drawing_number: string;
  title: string;
  discipline: string;
  heuristic: {
    drawing_number: string;
    title: string;
    discipline: string;
  };
  llm_used: boolean;
  metadata_source: string;
  ocr_provider: string;
  ocr_success: boolean;
}

export interface DrawingReferenceLink {
  sheet_id: string;
  drawing_number: string;
  title: string;
  discipline: string;
  pdf_file_id: string;
  pdf_page_number: number;
  sheet_revision_id: string;
  label: string;
}

export interface DrawingActivityItem {
  id: string;
  kind: 'operation' | 'audit';
  event_type: string;
  description: string;
  created_at: string;
  actor: User | null;
  page_number: number | null;
  layer_id: string | null;
  details: Record<string, any>;
}

// ============================================
// API Functions
// ============================================

/**
 * Upload a PDF file to the backend
 * @param file - The PDF file to upload
 * @param projectId - The project ID to associate the PDF with
 * @returns Promise with upload response
 */
export async function uploadPDF(file: File, projectId: number): Promise<PDFFile> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project', projectId.toString());
  formData.append('original_filename', file.name);

  const response = await POST<FormData>('/drawings/pdf-files/', formData);
  return response.data;
}

export async function createDrawingUploadBatch(
  projectId: number,
  files: Array<{ file_name: string; content_type: string; file_size: number }>
): Promise<DrawingUploadBatchInitResponse> {
  const response = await POST('/drawings/upload-batches/', {
    project_id: projectId,
    files,
  });
  return response.data;
}

export async function uploadDrawingBatchFile(
  signedUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/pdf');
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export async function finalizeDrawingUploadBatch(
  batchId: string,
  itemIds?: string[],
  failedItemIds?: string[]
): Promise<{
  status: string;
  finalized_count: number;
  failed_count: number;
  batch: DrawingUploadBatch;
}> {
  const response = await POST(`/drawings/upload-batches/${batchId}/finalize/`, {
    item_ids: itemIds,
    failed_item_ids: failedItemIds,
  });
  return response.data;
}

export async function getDrawingUploadBatch(batchId: string): Promise<DrawingUploadBatch> {
  const response = await GET<void>(`/drawings/upload-batches/${batchId}/`);
  return response.data;
}

export async function retryFailedDrawingUploadBatch(batchId: string): Promise<{
  status: string;
  requeued_count?: number;
  batch: DrawingUploadBatch;
}> {
  const response = await POST<Record<string, never>>(`/drawings/upload-batches/${batchId}/retry_failed/`, {});
  return response.data;
}

export async function patchPDFPageMetadata(
  pageId: string,
  updates: Partial<Pick<PDFPage, 'drawing_number' | 'title' | 'discipline'>>
): Promise<{ page: PDFPage }> {
  const response = await PATCH(`/drawings/pdf-pages/${pageId}/metadata/`, updates);
  return response.data;
}

export async function previewUploadPDFMetadata(file: File, projectId: number): Promise<PDFUploadMetadataPreview> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project', String(projectId));
  const response = await POST<FormData>('/drawings/pdf-files/metadata_preview/', formData);
  return response.data;
}

/**
 * Get a specific PDF file from the backend
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with PDF file details
 */
export async function getPDF(pdfId: string): Promise<PDFFile> {
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/`);
  return response.data;
}

export async function getPDFPage(pageId: string): Promise<PDFPage> {
  const response = await GET<void>(`/drawings/pdf-pages/${pageId}/`);
  return response.data;
}

/**
 * Get all PDF files for a project
 * @param projectId - Optional project ID to filter by
 * @returns Promise with array of PDF files (file_url is a direct signed GCS URL)
 */
export async function listPDFs(projectId?: number): Promise<PDFFileList[]> {
  let endpoint = projectId
    ? `/drawings/pdf-files/?project=${projectId}`
    : '/drawings/pdf-files/';

  const allResults: PDFFileList[] = [];
  while (endpoint) {
    const response = await GET<void>(endpoint);
    const data = response.data as any;
    if (data && typeof data === 'object' && 'results' in data) {
      allResults.push(...(data.results as PDFFileList[]));
      endpoint = data.next ? toApiRelativeEndpoint(data.next) : '';
    } else {
      return data as PDFFileList[];
    }
  }

  return allResults;
}

function toApiRelativeEndpoint(url: string): string {
  const parsed = new URL(url, window.location.origin);
  return `${parsed.pathname}${parsed.search}`.replace(/^\/api\/v1/, '') || '/';
}

/**
 * Get all PDFs for a specific project (alternative endpoint)
 * @param projectId - The project ID
 * @returns Promise with project PDFs
 */
export async function getProjectPDFs(projectId: number): Promise<{
  project_id: number;
  project_name: string;
  pdfs: PDFFileList[];
}> {
  const response = await GET<void>(`/drawings/projects/${projectId}/pdfs/`);
  return response.data;
}

/**
 * Delete a PDF file from the backend
 * @param pdfId - The UUID of the PDF file to delete
 * @returns Promise<void>
 */
export async function deletePDF(pdfId: string): Promise<void> {
  await DELETE<void>(`/drawings/pdf-files/${pdfId}/`);
}

/**
 * Get the layer document associated with a PDF
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with layer document details
 */
export async function getLayerDocument(pdfId: string): Promise<LayerDocument> {
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/layer_document/`);
  return response.data;
}

/**
 * Create a new layer
 * @param layerData - The layer data to create
 * @returns Promise with created layer
 */
export async function createLayer(
  layerData: Omit<Layer, 'id' | 'created_at' | 'updated_at' | 'created_by'>,
  options: LayerMutationOptions = {}
): Promise<Layer> {
  const response = await POST('/drawings/layers/', {
    ...layerData,
    ...options,
  });
  return response.data;
}

/**
 * Update an existing layer
 * @param layerId - The UUID of the layer
 * @param updates - Partial layer data to update
 * @returns Promise with updated layer
 */
export async function updateLayer(
  layerId: string,
  updates: Partial<Pick<Layer, 'data' | 'z_index' | 'visible' | 'markup_scope' | 'punch_list_item' | 'observation' | 'submittal' | 'rfi'>>,
  options: LayerMutationOptions = {}
): Promise<Layer> {
  const response = await PATCH(`/drawings/layers/${layerId}/`, {
    ...updates,
    ...options,
  });
  return response.data;
}

/**
 * Delete a layer
 * @param layerId - The UUID of the layer to delete
 * @returns Promise<void>
 */
export async function deleteLayer(layerId: string, options: LayerMutationOptions = {}): Promise<void> {
  const params = new URLSearchParams();
  if (options.expected_version !== undefined) {
    params.set('expected_version', String(options.expected_version));
  }
  if (options.client_mutation_id) {
    params.set('client_mutation_id', options.client_mutation_id);
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  await DELETE<void>(`/drawings/layers/${layerId}/${suffix}`);
}

/**
 * Get all layers for a layer document
 * @param layerDocumentId - The UUID of the layer document
 * @param pageNumber - Optional page number to filter by
 * @returns Promise with array of layers
 */
export async function listLayers(
  layerDocumentId: string,
  pageNumber?: number,
  markupScope?: 'all' | 'personal' | 'shared'
): Promise<Layer[]> {
  let endpoint = `/drawings/layers/?layer_document=${layerDocumentId}`;
  if (pageNumber !== undefined) {
    endpoint += `&page_number=${pageNumber}`;
  }
  if (markupScope && markupScope !== 'all') {
    endpoint += `&markup_scope=${markupScope}`;
  }
  
  const response = await GET<void>(endpoint);
  
  // Handle paginated response from DRF
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  
  return response.data as any;
}

export async function listLayerHistory(layerId: string): Promise<{
  layer_id: string;
  count: number;
  results: Array<{
    id: string;
    op_type: string;
    payload: Record<string, unknown>;
    base_version: number;
    created_by: User | null;
    created_at: string;
  }>;
}> {
  const response = await GET<void>(`/drawings/layers/${layerId}/history/`);
  return response.data;
}

export async function listPunchItems(projectId: number, search?: string): Promise<PunchListItemRef[]> {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (search) params.set('search', search);
  const response = await GET<void>(`/punchlists/items/?${params.toString()}`);
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  return response.data as any;
}

export async function listObservations(projectId: number, search?: string): Promise<ObservationRef[]> {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (search) params.set('search', search);
  const response = await GET<void>(`/observations/items/?${params.toString()}`);
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  return response.data as any;
}

export async function listSubmittals(projectId: number, search?: string): Promise<SubmittalRef[]> {
  const params = new URLSearchParams({ project_id: String(projectId), current_revision_only: 'true' });
  if (search) params.set('search', search);
  const response = await GET<void>(`/submittals/items/?${params.toString()}`);
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  return response.data as any;
}

export async function listRFIs(projectId: number, search?: string): Promise<RFIRef[]> {
  const params = new URLSearchParams({ project_id: String(projectId) });
  if (search) params.set('search', search);
  const response = await GET<void>(`/rfis/items/?${params.toString()}`);
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  return response.data as any;
}

/**
 * Trigger PDF page processing
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with processing status
 */
export async function processPDFPages(pdfId: string): Promise<{ status: string; task_id?: string; message: string }> {
  const response = await POST<Record<string, never>>(`/drawings/pdf-files/${pdfId}/process_pages/`, {});
  return response.data;
}

export async function getPDFProcessingStatus(pdfId: string): Promise<{
  pdf_file: string;
  status: PDFFile['status'];
  processing_error: string;
  page_count: number;
  processed_pages: number;
  is_complete: boolean;
}> {
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/processing_status/`);
  return response.data;
}

export async function getPDFRevisions(pdfId: string, pageNumber?: number): Promise<{
  pdf_file: string;
  page_number?: number;
  sheet_id?: string;
  sheet_identity_key?: string;
  drawing_number?: string;
  title?: string;
  discipline?: string;
  group_key?: string;
  count: number;
  results: PDFRevisionOption[];
}> {
  const params = new URLSearchParams();
  if (pageNumber !== undefined) {
    params.set('page_number', String(pageNumber));
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/revisions/${query}`);
  return response.data;
}

export async function getPDFReferences(pdfId: string, pageNumber?: number): Promise<{
  pdf_file: string;
  page_number: number;
  sheet_id?: string;
  drawing_number?: string;
  title?: string;
  reference_numbers_found: string[];
  count: number;
  results: DrawingReferenceLink[];
}> {
  const params = new URLSearchParams();
  if (pageNumber !== undefined) {
    params.set('page_number', String(pageNumber));
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/references/${suffix}`);
  return response.data;
}

export async function listPDFPages(pdfId: string, pageNumber?: number, needsSheetReview?: boolean): Promise<PDFPage[]> {
  const params = new URLSearchParams({ pdf_file: pdfId });
  if (pageNumber !== undefined) params.set('page_number', String(pageNumber));
  if (needsSheetReview) params.set('needs_sheet_review', 'true');
  const response = await GET<void>(`/drawings/pdf-pages/?${params.toString()}`);
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  return response.data as any;
}

export async function updatePDFPage(
  pageId: string,
  updates: Partial<Pick<PDFPage, 'rotation'>>
): Promise<PDFPage> {
  const response = await PATCH(`/drawings/pdf-pages/${pageId}/`, updates);
  return response.data;
}

export async function listProjectDrawingSheets(pdfId: string, search?: string): Promise<{
  pdf_file: string;
  count: number;
  results: DrawingSheetOption[];
}> {
  const params = new URLSearchParams();
  if (search) params.set('search', search);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/sheets/${suffix}`);
  return response.data;
}

export async function publishSheetRevision(pdfId: string, sheetRevisionId: string): Promise<{
  status: string;
  message: string;
  revision: SheetRevisionDetail;
}> {
  const response = await POST(`/drawings/pdf-files/${pdfId}/publish_revision/`, {
    sheet_revision_id: sheetRevisionId,
  });
  return response.data;
}

export async function matchPDFPageToSheet(pageId: string, payload: {
  sheet_id?: string;
  create_new_sheet?: boolean;
  drawing_number?: string;
  title?: string;
  discipline?: string;
  revision_number?: number;
  revision_label?: string;
  set_current?: boolean;
}): Promise<{
  status: string;
  page: PDFPage;
  revision: SheetRevisionDetail;
}> {
  const response = await POST(`/drawings/pdf-pages/${pageId}/match_sheet/`, payload);
  return response.data;
}

export async function getDrawingLog(projectId?: number, search?: string): Promise<{
  count: number;
  results: Array<{
    pdf_file_id: string;
    project_id: number;
    project_name: string;
    pdf_filename: string;
    page_number: number;
    drawing_number: string;
    title: string;
    discipline: string;
    status: PDFFile['status'];
  }>;
}> {
  const params = new URLSearchParams();
  if (projectId !== undefined) params.set('project', String(projectId));
  if (search) params.set('search', search);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await GET<void>(`/drawings/pdf-files/drawing_log/${query}`);
  return response.data;
}

export async function getPDFActivity(pdfId: string, limit = 50): Promise<{
  pdf_file: string;
  count: number;
  results: DrawingActivityItem[];
}> {
  const params = new URLSearchParams({ limit: String(limit) });
  const response = await GET<void>(`/drawings/pdf-files/${pdfId}/activity/?${params.toString()}`);
  return response.data;
}

/**
 * Request PDF export with layers flattened
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with export job status
 */
export async function requestPDFExport(pdfId: string): Promise<{
  status: string;
  message: string;
  pdf_file: string;
  version: number;
}> {
  const response = await POST<{ pdf_file: string }>('/drawings/exports/', { pdf_file: pdfId });
  return response.data;
}

/**
 * List all exports for a PDF
 * @param pdfId - The UUID of the PDF file
 * @returns Promise with array of exports
 */
export async function listPDFExports(pdfId: string): Promise<any[]> {
  const response = await GET<void>(`/drawings/exports/?pdf_file=${pdfId}`);
  
  // Handle paginated response from DRF
  if (response.data && typeof response.data === 'object' && 'results' in response.data) {
    return (response.data as any).results;
  }
  
  return response.data as any;
}
